import React, { useEffect, useState, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { ProgressSpinner } from 'primereact/progressspinner';
import AddLocationForm from './AddLocationForm';
import LabelName from 'components/UI/LabelName/LabelName';

import { useCalendarContext } from 'modules/calendar/Context/CalendarContext';
import { useConferenceRoomAvailability } from 'hooks/useConferenceRoomAvailability';

function EventLocation({ control, setValue, watch }) {
  const { locationsList, officeLocationsList, editEventModal, eventDetails } = useCalendarContext();
  const [addLocation, setAddLocation] = useState(false);

  // Watch form values
  const selectedLocation = watch('location');
  const meetingStartTime = watch('meeting_start_time');
  const meetingEndTime = watch('meeting_end_time');
  const currentConferenceRoomId = watch('conference_room_id');

  // Check if selected location is an office location
  const isOfficeLocation = useMemo(() => {
    if (!selectedLocation || !officeLocationsList?.length) return false;

    // Check if the selected location value matches any office location ID
    return officeLocationsList.some((office) => String(office.office_location_id) === String(selectedLocation));
  }, [selectedLocation, officeLocationsList]);

  // Use the availability hook without auto-checking on time changes
  // Pass current event ID to exclude it from availability conflicts during edit
  const currentEventId = editEventModal && eventDetails?.pk ? eventDetails.pk : null;
  const { conferenceRooms, isLoading, error, refreshAvailability } = useConferenceRoomAvailability(
    isOfficeLocation ? selectedLocation : null, // Only pass location if it's an office location
    meetingStartTime,
    meetingEndTime,
    {
      autoCheckAvailability: false, // Don't auto-check when rooms load
      checkOnTimeChange: false, // Don't auto-check when time changes
    },
    currentEventId
  );

  // In edit mode, check if the current room still exists in the available list
  const conferenceRoomOptions = useMemo(() => {
    if (!editEventModal || !eventDetails) {
      return conferenceRooms;
    }

    const currentRoomId = eventDetails.conference_room_id || eventDetails.conference_room;
    if (!currentRoomId) {
      return conferenceRooms;
    }

    // Check if current room is already in the list
    const roomExists = conferenceRooms.some((room) => room.value === currentRoomId);
    if (roomExists) {
      return conferenceRooms;
    }

    // If room doesn't exist in current list, it means it was deleted from office location
    // Return empty options to show empty dropdown
    return [];
  }, [conferenceRooms, editEventModal, eventDetails]);

  useEffect(() => {
    // Reset previously selected conference room if location changes (but not during initial edit load)
    // Only reset if switching away from office location or if not an office location
    if (!editEventModal && !isOfficeLocation && currentConferenceRoomId) {
      setValue('conference_room_id', '');
    }
  }, [selectedLocation, editEventModal, isOfficeLocation, currentConferenceRoomId, setValue]);

  // Only refresh availability when user is actually selecting a conference room
  // This will be called manually when the dropdown is opened or when user selects a room

  // Re-set conference room value after rooms are loaded (for edit mode)
  useEffect(() => {
    if (editEventModal && eventDetails && !currentConferenceRoomId) {
      const conferenceRoomId = eventDetails.conference_room_id || eventDetails.conference_room;
      if (conferenceRoomId) {
        // Check if the room still exists in the current list
        const roomExists = conferenceRooms.some((room) => room.value === conferenceRoomId);
        if (roomExists && currentConferenceRoomId !== conferenceRoomId) {
          setValue('conference_room_id', conferenceRoomId);
        } else if (!roomExists && currentConferenceRoomId !== '') {
          // Room was deleted from office location, clear the value
          setValue('conference_room_id', '');
        }
      }
    }
  }, [editEventModal, eventDetails, conferenceRooms, currentConferenceRoomId, setValue]);

  // Clear conference room if it's no longer valid (for edit mode)
  useEffect(() => {
    if (editEventModal && eventDetails && currentConferenceRoomId) {
      const roomExists = conferenceRooms.some((room) => room.value === currentConferenceRoomId);
      if (!roomExists && currentConferenceRoomId !== '') {
        // Room was deleted from office location, clear the value
        setValue('conference_room_id', '');
      }
    }
  }, [editEventModal, eventDetails, conferenceRooms, currentConferenceRoomId, setValue]);

  return (
    <div className="d-flex py-2 flex-wrap">
      <div className="col-md-4 col-12 mt-2">
        <LabelName required={false} htmlFor="location">
          Location
        </LabelName>
      </div>
      <div className="col-md-7 col-11 align-items-center">
        {addLocation ? (
          <AddLocationForm setAddLocation={setAddLocation} setValue={setValue} />
        ) : (
          <Controller
            name="location"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Dropdown
                id={field.name}
                value={field.value}
                onChange={(e) => {
                  field.onChange(e.value);
                }}
                options={locationsList}
                placeholder="Select Location"
                className="w-100"
                filter
                showClear={field.value ? true : false}
                optionLabel="label"
                optionValue="value"
              />
            )}
          />
        )}
      </div>
      <div className="col-sm-1 d-flex justify-content-end mt-2 ps-2">
        <span className="pointer">
          <i className="fas fa-plus-circle location-add" onClick={() => setAddLocation(true)}></i>
          <Tooltip content="Add Location" position="top" target=".location-add" showDelay={500} />
        </span>
      </div>
      {selectedLocation && isOfficeLocation && (
        <>
          <div className="w-100 my-2"></div>
          <div className="col-md-4 col-12 mt-2">
            <LabelName required={false} htmlFor="conference_room_id">
              Conference Room
            </LabelName>
          </div>
          <div className="col-md-8 col-11 align-items-center">
            <Controller
              name="conference_room_id"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <div className="position-relative">
                  <Dropdown
                    id={field.name}
                    value={field.value}
                    onChange={(e) => {
                      // Prevent selection of unavailable rooms
                      const selectedRoom = conferenceRoomOptions.find((room) => room.value === e.value);
                      if (selectedRoom && selectedRoom.disabled) {
                        // Don't set the value for disabled rooms - they should already be disabled in the dropdown
                        return;
                      }
                      field.onChange(e.value);
                    }}
                    options={conferenceRoomOptions}
                    placeholder={isLoading ? 'Loading rooms...' : 'Select Conference Room'}
                    className="w-100"
                    filter
                    showClear={field.value ? true : false}
                    optionLabel="label"
                    optionValue="value"
                    disabled={isLoading}
                    optionDisabled="disabled"
                    itemTemplate={(option) => (
                      <div
                        style={{
                          color: option.disabled ? '#999' : 'inherit',
                          fontStyle: option.disabled ? 'italic' : 'normal',
                        }}
                      >
                        {option.dropdownLabel || option.label}
                      </div>
                    )}
                    onShow={() => {
                      // Check availability when user opens the dropdown
                      if (isOfficeLocation && selectedLocation && meetingStartTime && meetingEndTime) {
                        refreshAvailability();
                      }
                    }}
                  />
                  {isLoading && (
                    <div className="position-absolute" style={{ right: '30px', top: '50%', transform: 'translateY(-50%)' }}>
                      <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="4" />
                    </div>
                  )}
                </div>
              )}
            />
            {error && <small className="text-warning mt-1 d-block">{error}</small>}
          </div>
          <div className="col-sm-1 d-flex justify-content-end mt-2 ps-2"></div>
        </>
      )}
    </div>
  );
}

export default EventLocation;
